import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';
import { useTranslation } from '../../hooks/useTranslation';

const Footer = () => {
  const { siteContent } = useSiteContent();
  const { t } = useTranslation();
  const footer = siteContent?.footer || {};
  const contactPage = siteContent?.contactPage || {};

  if (footer.visible === false) return null;

  const locations = contactPage.locations || ['Cebu, Philippines', 'London, UK'];

  return (
    <footer className="bg-slate-100">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
          {/* Quick Links */}
          <div>
            <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4">
              {footer.quickLinksTitle || 'Quick Links'}
            </h3>
            <ul className="space-y-2.5">
              <li>
                <Link to="/programs" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
                  {t('nav.programs')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Get in Touch */}
          <div>
            <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4">
              {footer.getInTouchTitle || 'Get in Touch'}
            </h3>
            <ul className="space-y-2.5">
              <li>
                <a 
                  href={`mailto:${contactPage.email || 'contact@englishpassion.uk'}`}
                  className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span>{contactPage.email || 'contact@englishpassion.uk'}</span>
                </a>
              </li>
              {locations.map((location, index) => (
                <li key={`footer-loc-${location.slice(0, 10)}-${index}`} className="flex items-start gap-2 text-sm text-slate-500">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{location}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-4">
              {footer.legalTitle || 'Legal'}
            </h3>
            <ul className="space-y-2.5">
              <li>
                <button className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
                  {footer.privacyText || 'Privacy Policy'}
                </button>
              </li>
              <li>
                <button className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
                  {footer.termsText || 'Terms of Service'}
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-400 mb-3 italic">
            {footer.missionStatement || 'We help learners grow with confidence through personalized, high-quality English instruction.'}
          </p>
          <p className="text-xs sm:text-sm text-slate-400">
            {footer.copyright || '© 2026 English Passion. All rights reserved.'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
